import Slate from 'slate';
import MarkdownIt from 'markdown-it';
import videoPlugin from '@centerforopenscience/markdown-it-video';
import trimTrailingLines from 'trim-trailing-lines';

import { analytics } from 'core/analytics';

import { videoInfoFromURL } from 'libs/editor/views/mediaInput';
import { parser } from 'libs/editor/serializers/markup-it';

import { html } from 'libs/editor/serializers/html';
import { buildMatchData } from 'libs/editor/plugins/features/linkify';
import LinkifyIt from 'linkify-it';

const emptyTrailRegex = /([\u200B-\u200D\uFEFF]*\n)+$/;
const linkify = LinkifyIt();

const trimParagraphs = string => string.replace(emptyTrailRegex, '').trimEnd();

/*
  Equivalence table MD <=> Slate

  - Text nodes are already combined by a special rule
  - List items contain a paragraph and then an inline

  ------------------------------------------------------------------
  |           MD           |                 Slate                 |
  |------------------------|---------------------------------------|
  | text (inline)          | text (object) => leaves               |
  | softbreak (inline)     | text (combine if possible)            |
  | hardbreak (inline)     | text (combine if possible)            |
  | escape (inline)        | text (combine if possible)            |
  | code_inline (inline)   | code (mark)                           |
  | strikethrough (inline) | strikethrough (mark)                  |
  | strong (inline)        | bold (mark)                           |
  | em (inline)            | italic (mark)                         |
  | link (inline)          | link (inline)                         |
  | image (inline)         | image (inline)                        |
  | autolink (inline)      | link (inline)                         |
  | html_inline (inline)   | html_inline? (inline)                 |
  | entity (inline)        | text (combine if possible, unescape)  |
  |------------------------|---------------------------------------|
  | table (block)          | table (block)                         |
  | thead (block)          | -                                     |
  | tbody (block)          | -                                     |
  | tr (block)             | table_row (block)                     |
  | td (block)             | table_cell (block)                    |
  | th (block)             | table_cell (block)                    |
  | code (block)           | code (block)                          |
  | fence (block)          | code (block)                          |
  | blockquote (block)     | blockquote (block)                    |
  | hr (block)             | horizontal_rule (block)               |
  | ordered_list (block)   | ordered_list (block)                  |
  | unordered_list (block) | unordered_list (block)                |
  | list_item (block)      | list_item (block)                     |
  | reference (block)      | reference (block)                     |
  | heading (block)        | h{n} (block)                          |
  | lheading (block)       | line_heading (block)                  |
  | html_block (block)     | html_block (block)                    |
  | paragraph (block)      | paragraph (block)                     |
  |------------------------|---------------------------------------|
  | normalize (core)       | normalized input string (\n and \0)   |
  | block (core)           | can be any of the different blocks    |
  | inline (core)          | it's a container for other inlines    |
  | linkify (core)         | generates links from implicit link    |
  | replacements (core)    | replace notations for special chars   |
  | smartquotes (core)     | straight quote marks to typographic   |
  ------------------------------------------------------------------

*/

const textyNodes = ['text', 'code_inline', 'strong_open', 'em_open'];

// FIXME - Use regex
const headingType = tag => {
  // eslint-disable-next-line
  switch (tag) {
    case 'h1':
      return 'header_one';
    case 'h2':
      return 'header_two';
    case 'h3':
      return 'header_three';
    default:
    case 'h4':
      return 'header_four';
  }
};

const token2node = (token, nextToken = {}) => {
  // eslint-disable-next-line
  switch (token.type) {
    case 'heading_open':
      return {
        object: 'block',
        type: headingType(token.tag),
        isVoid: false,
        nodes: [],
        data: {},
      };
    case 'bullet_list_open':
      return {
        object: 'block',
        type: 'unordered_list',
        isVoid: false,
        nodes: [],
      };
    case 'ordered_list_open':
      return {
        object: 'block',
        type: 'ordered_list',
        isVoid: false,
        nodes: [],
      };
    case 'list_item_open':
      return {
        object: 'block',
        type: 'list_item',
        isVoid: false,
        nodes: [],
      };
    case 'blockquote_open':
      return {
        object: 'block',
        type: 'blockquote',
        isVoid: false,
        nodes: [],
      };
    case 'paragraph_open': {
      const nodes =
        nextToken.type === 'inline' &&
        nextToken.children.length &&
        !textyNodes.includes(nextToken.children[0].type)
          ? [token2node({ type: 'text', content: '' })]
          : [];
      return {
        object: 'block',
        type: 'paragraph',
        isVoid: false,
        nodes,
        data: {},
      };
    }
    case 'link_open': {
      let type = 'link';
      let data = { href: token.attrs[0][1] };
      if (token.markup === 'linkify') {
        type = 'linkified-link';
        const match = linkify.match(nextToken.content)[0];
        data = buildMatchData(match);
      }
      return {
        object: 'inline',
        nodes: [],
        type,
        data,
      };
    }
    case 'code_block':
    case 'fence': {
      const code_lines = trimTrailingLines(token.content)
        .split('\n')
        .map(line => {
          return {
            object: 'block',
            type: 'code_line',
            data: {},
            nodes: [token2node({ type: 'text', content: line })],
          };
        });
      return {
        object: 'block',
        type: 'code_block',
        data: {},
        nodes: code_lines,
      };
    }
    case 'code_inline':
      return {
        object: 'text',
        leaves: [
          {
            object: 'leaf',
            text: token.content,
            marks: [
              {
                object: 'mark',
                type: 'CODE',
                data: {},
              },
            ],
          },
        ],
      };
    case 'strong_open':
      return {
        object: 'mark',
        type: 'BOLD',
        nodes: [],
      };
    case 'em_open':
      return {
        object: 'mark',
        type: 'ITALIC',
        nodes: [],
      };
    case 'softbreak':
    case 'hardbreak': {
      return {
        object: 'inline',
        type: token.type,
        isVoid: true,
        nodes: [token2node({ type: 'text', content: '' })],
        data: {},
      };
    }
    case 'image': {
      const data = {};
      const src = token.attrs[0][1];
      const alt = token.attrs[1][1] || token.content;
      if (src) data.src = src;
      if (alt) data.alt = alt;
      return {
        object: 'inline',
        type: 'image',
        isVoid: true,
        nodes: [token2node({ type: 'text', content: '' })],
        data,
      };
    }
    case 'video': {
      // FIXME - Use our own plugin
      let src = token.videoID;
      if (token.service === 'vimeo') {
        const { key } = videoInfoFromURL(src);
        src = key;
      }
      const data = {
        src,
        site: token.service,
      };
      return {
        object: 'inline',
        type: 'video',
        isVoid: true,
        nodes: [token2node({ type: 'text', content: '' })],
        data,
      };
    }
    case 'hr':
      return {
        data: {},
        nodes: [
          {
            leaves: [
              {
                marks: [],
                object: 'leaf',
                text: '',
              },
            ],
            object: 'text',
          },
        ],
        object: 'block',
        type: 'hr',
      };
    case 'html_block': {
      const value = html.deserialize(token.content).toJSON();
      return {
        object: 'block',
        type: 'html_block',
        nodes: value.document.nodes,
        data: {},
      };
    }
    case 'html_inline': {
      return token2node({ type: 'text', content: token.content });
    }
    case 'table_open': {
      return {
        object: 'block',
        type: 'table',
        data: {
          align: [],
        },
        nodes: [],
      };
    }
    case 'thead_open':
      return {
        object: 'block',
        type: 'table_header',
        data: {},
        nodes: [],
      };
    case 'tr_open': {
      return {
        object: 'block',
        type: 'table_row',
        data: {},
        nodes: [],
      };
    }
    case 'th_open':
    case 'td_open':
      return {
        object: 'block',
        type: 'table_cell',
        data: {},
        nodes: [],
      };
    case 'tbody_open': {
      return {
        object: 'block',
        type: 'table_body',
        data: {},
        nodes: [],
      };
    }
    case 'inline':
      if (token.nesting === 0) {
        return token2node({ type: 'text', content: token.content });
      }
    // eslint-disable-next-line no-fallthrough
    default: {
      // Unknown token - log, report and fall back to text
      const error = new Error('Unhandled markdown-it token');
      const scopingFunction = scope => {
        scope.setExtra('token', JSON.stringify(token));
      };
      analytics.logError(error, scopingFunction);
    }
    // eslint-disable-next-line no-fallthrough
    case 'text': {
      const text = token.content.match(/^[\u200B]$/) ? '' : token.content;
      return {
        object: 'text',
        leaves: [
          {
            object: 'leaf',
            marks: [],
            text,
          },
        ],
      };
    }
  }
};

const squashText = current => {
  const nodesCopy = [...current.nodes];
  let currentText = null;
  current.nodes = [];
  // eslint-disable-next-line
  for (const node of nodesCopy) {
    if (node.object === 'text') {
      if (!currentText) {
        currentText = token2node({ type: 'text', content: '' });
      }
      currentText.leaves.push(...node.leaves);
    } else {
      if (currentText) {
        current.nodes.push(currentText);
        currentText = null;
      }
      current.nodes.push(node);
    }
  }
  if (currentText) {
    current.nodes.push(currentText);
  }
};

const splitBreaks = (parent, current) => {
  const hasBreaks = current.nodes.find(node =>
    ['softbreak', 'hardbreak'].includes(node.type),
  );
  if (!hasBreaks) return;
  const nodeGroups = [];
  let nodeAcc = [];
  // eslint-disable-next-line
    for (const node of current.nodes) {
    if (['softbreak', 'hardbreak'].includes(node.type)) {
      nodeGroups.push(nodeAcc);
      nodeAcc = [];
    } else {
      nodeAcc.push(node);
    }
  }
  if (nodeAcc.length) {
    nodeGroups.push(nodeAcc);
  }
  parent.nodes.pop();
  const newNodes = nodeGroups.map(nodes => {
    const { object, type, isVoid, data } = current;
    return {
      object,
      type,
      data,
      nodes,
      isVoid,
    };
  });
  parent.nodes.push(...newNodes);
};

const tokens2nodes = (parent, tokenList) => {
  const nesting = [parent];
  // eslint-disable-next-line
  for (const [i, token] of tokenList.entries()) {
    const nextToken = i < tokenList.length - 1 ? tokenList[i + 1] : {};
    const current = nesting[nesting.length - 1];
    switch (token.nesting) {
      case -1: {
        // TODO - If current container was a 'mark', we need to flatten it and apply it to the children
        if (current.object === 'mark') {
          const { type, nodes } = current;
          // eslint-disable-next-line
          for (const node of nodes) {
            const leaves = node.leaves || [];
            // eslint-disable-next-line
            for (const leaf of leaves) {
              leaf.marks.push({ object: 'mark', type, data: {} });
            }
          }
          const newCurrent = nesting[nesting.length - 2];
          newCurrent.nodes.pop();
          newCurrent.nodes = [...newCurrent.nodes, ...current.nodes];
        }
        if (
          current.type === 'list_item' &&
          current.nodes.length &&
          current.nodes[0].object === 'block'
        ) {
          // eslint-disable-next-line
          for (const node of current.nodes) {
            node.type = node.type === 'paragraph' ? 'unstyled' : node.type;
          }
        }
        if (current.type === 'table') {
          const [header, body] = current.nodes;
          current.nodes = [...header.nodes, ...body.nodes];
        }
        if (current.type === 'paragraph') {
          const { nodes } = current;
          if (
            nodes.length > 1 &&
            nodes[nodes.length - 1].object === 'text' &&
            nodes[nodes.length - 1].leaves.length === 1 &&
            nodes[nodes.length - 1].leaves[0].text.match(/^[\s\u200B]?$/)
          ) {
            nodes.pop();
          }
          if (
            nodes.length === 2 &&
            nodes[0].object === 'text' &&
            nodes[0].leaves.length === 1 &&
            nodes[0].leaves[0].text === '' &&
            nodes[1].object === 'inline' &&
            ['image', 'video'].includes(nodes[1].type)
          ) {
            current.type = `${nodes[1].type}-block`;
            current.nodes = nodes[1].nodes;
            current.data = nodes[1].data;
            current.isVoid = true;
          } else if (
            // Add text to empty paragraph nodes
            nodes[nodes.length - 1] &&
            nodes[nodes.length - 1].object !== 'text'
          ) {
            nodes.push(token2node({ type: 'text', content: '' }));
          }
        }
        if (current.object === 'block') {
          squashText(current);
          const currentParent = nesting[nesting.length - 2];
          splitBreaks(currentParent, current);
        }
        if (current.nodes.length === 0) {
          current.nodes = [token2node({ type: 'text', content: '' })];
        }
        nesting.pop();
        break;
      }
      case 1: {
        const container = token2node(token, nextToken);
        // FIXME - Better handling of HTML blocks
        if (!container.type !== 'html_block') {
          current.nodes.push(container);
          nesting.push(container);
        }
        break;
      }
      default:
      case 0: {
        if (token.type !== 'image' && token.children && token.children.length) {
          tokens2nodes(current, token.children);
        } else {
          const builtToken = token2node(token);
          if (builtToken) {
            if (builtToken.type === 'html_block') {
              current.nodes.push(...builtToken.nodes);
            } else {
              current.nodes.push(builtToken);
            }
          }
        }
        break;
      }
    }
  }
};

export const markdown = {
  deserialize: content => {
    const md = new MarkdownIt({
      html: true,
      linkify: true,
      typographer: false,
    }).use(videoPlugin);
    const tokenizedMarkdown = md.parse(trimParagraphs(content), {});
    const document = { object: 'document', data: {}, nodes: [] };
    tokens2nodes(document, tokenizedMarkdown);
    return Slate.Value.create({ document });
  },
  serialize: value => {
    // FIXME - Still using markup-it for serialization. Replace as soon as possible.
    const inputDocument = value.document;
    const out = parser.serializeDocument(inputDocument);
    return trimParagraphs(out);
  },
};
